import type { CalcKey } from '../locales/keys';

export interface TaylorPreset {
  id: string;
  labelKey: CalcKey;
  /** The function f(x) as a parseable expression string. */
  source: string;
  /** Expansion center. */
  center: number;
  /** Visible domain [lo, hi] for the plot — wide enough to show divergence outside the radius. */
  domain: [number, number];
  /**
   * Radius of convergence.
   * `null` means ∞ (the series converges everywhere).
   * A finite positive number means the series converges only on (center−r, center+r).
   */
  convergenceRadius: number | null;
}

export const TAYLOR_PRESETS: TaylorPreset[] = [
  {
    id: 'exp',
    labelKey: 'preset.taylor.exp',
    // e^x — Maclaurin series converges everywhere (radius = ∞).
    source: 'exp(x)',
    center: 0,
    domain: [-3, 3],
    convergenceRadius: null,
  },
  {
    id: 'sin',
    labelKey: 'preset.taylor.sin',
    // sin(x) — Maclaurin series converges everywhere (radius = ∞).
    source: 'sin(x)',
    center: 0,
    domain: [-4, 4],
    convergenceRadius: null,
  },
  {
    id: 'cos',
    labelKey: 'preset.taylor.cos',
    // cos(x) — Maclaurin series converges everywhere (radius = ∞).
    source: 'cos(x)',
    center: 0,
    domain: [-4, 4],
    convergenceRadius: null,
  },
  {
    id: 'ln1px',
    labelKey: 'preset.taylor.ln1px',
    // ln(1+x) — radius of convergence 1; converges on (-1, 1].
    // Domain extends past ±1 so the learner sees divergence on the left.
    source: 'ln(1 + x)',
    center: 0,
    domain: [-2, 3],
    convergenceRadius: 1,
  },
  {
    id: 'geometric',
    labelKey: 'preset.taylor.geometric',
    // 1/(1-x) — geometric series; radius of convergence 1.
    // Domain extends past ±1 so divergence at |x| ≥ 1 is visible.
    source: '1 / (1 - x)',
    center: 0,
    domain: [-2, 2],
    convergenceRadius: 1,
  },
  {
    id: 'atan',
    labelKey: 'preset.taylor.atan',
    // atan(x) — radius of convergence 1; converges on [-1, 1].
    // Domain extends past ±1 to show where partial sums diverge.
    source: 'atan(x)',
    center: 0,
    domain: [-2, 2],
    convergenceRadius: 1,
  },
];
